import React, {useEffect, useLayoutEffect, useState} from 'react';
import {
    Button,
    Card,
    CardBody,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
    Typography
} from "@material-tailwind/react";
import axios from "axios";
import LotSummary from "./lot_summary";
import RoadSelect from "../../shared/road_select";

// Motivo 30 - CORRECCION DE DEDO: corrección de errores de captura sin efecto fiscal.
// Permite corregir dirección, R.P.P., notas y el nombre del contribuyente asignado
// al predio (p. ej. "jyan" -> "juan"). No recalcula avalúo ni adeudos.
const DataEntryCorrection = ({lot, movement}) => {
    const [lotNotes, setLotNotes] = useState("");
    const [openEndDialog, setOpenEndDialog] = useState(false);
    const [loaderActive, setLoaderActive] = useState(false);
    const [error, setError] = useState(null);
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [address, setAddress] = useState({
        road_id: "",
        road_name: "",
        ext_num: "",
        int_num: "",
        neighborhood_id: "",
        address_references: ""
    });
    const [registry, setRegistry] = useState({
        inscription: "",
        book: "",
        folio: "",
    });
    const [contributor, setContributor] = useState({
        name: "",
        parental_last_name: "",
        maternal_last_name: "",
    });

    useLayoutEffect(() => {
        setAddress({
            road_id: lot.road_id,
            road_name: lot.road_name,
            ext_num: lot.ext_num,
            int_num: lot.int_num,
            neighborhood_id: lot.neighborhood_id,
            address_references: lot.address_references,
        });
        setRegistry({
            inscription: lot.inscription,
            book: lot.book,
            folio: lot.folio,
        });
        setContributor({
            name: lot.name,
            parental_last_name: lot.parental_last_name,
            maternal_last_name: lot.maternal_last_name,
        });
        setLotNotes(lot.notes);
    }, [lot]);

    useEffect(() => {
        async function fetchSelectsData() {
            try {
                const neighborhoodsResponse = await fetch(`/neighborhoods.json`);

                if (neighborhoodsResponse.ok) {
                    const neighborhoodsData = await neighborhoodsResponse.json();
                    setNeighborhoods(neighborhoodsData);
                } else {
                    console.log("No se encontraron colonias para este código.");
                }
            } catch (err) {
                console.error("Error al buscar el predio:", err);
            }
        }

        fetchSelectsData();
    }, []);

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    const handleReset = () => {
        window.location.replace('movements');
    };

    // Un solo request: contribuyente + predio en la misma transacción (patrón del motivo 19).
    // El backend valida que el predio pertenezca al contribuyente asignado.
    const handleSubmit = async () => {
        try {
            setLoaderActive(true);
            const response = await axios.patch(`contributors/${lot.contributor_id}.json`, {
                contributor: {
                    name: contributor.name,
                    parental_last_name: contributor.parental_last_name,
                    maternal_last_name: contributor.maternal_last_name,
                    lots_attributes: [{
                        parcel_code: lot.parcel_code,
                        road_id: address.road_id,
                        neighborhood_id: address.neighborhood_id,
                        ext_num: address.ext_num,
                        int_num: address.int_num,
                        address_references: address.address_references,
                        inscription: registry.inscription,
                        book: registry.book,
                        folio: registry.folio,
                        notes: lotNotes,
                        reason_type_id: movement.id,
                    }],
                },
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    "X-CSRF-Token": getCSRFToken(),
                }
            });
            if (response.status === 200) {
                setOpenEndDialog(true);
                const url = `/lots/${lot.parcel_code}/print_movement.pdf`;
                window.open(url, '_blank');
                setLoaderActive(false);
            } else {
                setError('Error al corregir los datos del predio. Verifica los datos.');
                setLoaderActive(false);
            }
        } catch (err) {
            setError('Error al guardar. Verifica los datos.');
            setLoaderActive(false);
        }
    };

    return (
        <>
            <div className="flex-auto py-6 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Información del predio
                </h6>
                <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                    <div
                        className="flex-grow border border-blue-gray-100 p-4 rounded-t-none rounded-b-lg w-full lg:w-1/2 overflow-x-hidden">
                        <LotSummary lot={lot}/>
                    </div>
                </div>
            </div>
            <div className="flex-auto py-10 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Corrección de dedo
                </h6>
                <Card className='w-full border border-blue-gray-100 rounded-b-lg rounded-t-none' shadow={false}>
                    <CardBody className='p-4'>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-4 font-bold uppercase">
                            DIRECCIÓN DEL PREDIO
                        </h6>
                        <div className="flex flex-wrap gap-4">
                            <div className="relative w-full mb-3">
                                <label
                                    className="block uppercase text-blue-gray-600 text-xs font-bold mb-2">
                                    Vialidad
                                </label>
                                <RoadSelect
                                    value={
                                        address.road_id
                                            ? {
                                                label: address.road_name,
                                                value: address.road_id
                                            }
                                            : ''
                                    }
                                    onChange={(option) => {
                                        setAddress((prev) => ({
                                            ...prev,
                                            road_id: option.value,
                                            road_name: option.label,
                                        }));
                                    }}
                                />
                            </div>
                            <div className='flex flex-row gap-4 w-full'>
                                <Input value={address.ext_num || ""}
                                       onChange={(e) => setAddress((prev) => ({
                                           ...prev,
                                           ext_num: e.target.value,
                                       }))}
                                       label={'Núm. ext.'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                                <Input value={address.int_num || ""}
                                       onChange={(e) => setAddress((prev) => ({
                                           ...prev,
                                           int_num: e.target.value,
                                       }))}
                                       label={'Núm. int.'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                            </div>
                            <div className="relative w-full mb-3">
                                <label
                                    className="block uppercase text-blue-gray-600 text-xs font-bold mb-2">
                                    Colonia
                                </label>
                                <select
                                    value={address.neighborhood_id || ""}
                                    onChange={(e) => setAddress((prev) => ({
                                        ...prev,
                                        neighborhood_id: e.target.value,
                                    }))}
                                    className="w-full rounded-[7px] border-blue-gray-200"
                                >
                                    {neighborhoods.map((neighborhood) => (
                                        <option key={neighborhood.id}
                                                value={neighborhood.id}>
                                            {neighborhood.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="relative w-full mb-3">
                                <Input value={address.address_references || ""}
                                       onChange={(e) => setAddress((prev) => ({
                                           ...prev,
                                           address_references: e.target.value,
                                       }))}
                                       label={'Referencias'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                            </div>
                        </div>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-4 font-bold uppercase">
                            R.P.P.
                        </h6>
                        <div className='flex flex-row gap-4 w-full text-center'>
                            <Input value={registry.inscription || ""}
                                   onChange={(e) => setRegistry((prev) => ({
                                       ...prev,
                                       inscription: e.target.value,
                                   }))}
                                   label={'Inscripción'}
                                   containerProps={{
                                       className: '!min-w-0'
                                   }}
                            />
                            <Input value={registry.book || ""}
                                   onChange={(e) => setRegistry((prev) => ({
                                       ...prev,
                                       book: e.target.value,
                                   }))}
                                   label={'Libro'}
                                   containerProps={{
                                       className: '!min-w-0'
                                   }}
                            />
                            <Input value={registry.folio || ""}
                                   onChange={(e) => setRegistry((prev) => ({
                                       ...prev,
                                       folio: e.target.value,
                                   }))}
                                   label={'Folio'}
                                   containerProps={{
                                       className: '!min-w-0'
                                   }}
                            />
                        </div>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-4 font-bold uppercase">
                            NOMBRE DEL CONTRIBUYENTE
                        </h6>
                        {lot.contributor_type === 'P' ? (
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <Input value={contributor.name || ""}
                                       onChange={(e) => setContributor((prev) => ({
                                           ...prev,
                                           name: e.target.value,
                                       }))}
                                       label={'Nombre'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                                <Input value={contributor.parental_last_name || ""}
                                       onChange={(e) => setContributor((prev) => ({
                                           ...prev,
                                           parental_last_name: e.target.value,
                                       }))}
                                       label={'Apellido paterno'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                                <Input value={contributor.maternal_last_name || ""}
                                       onChange={(e) => setContributor((prev) => ({
                                           ...prev,
                                           maternal_last_name: e.target.value,
                                       }))}
                                       label={'Apellido materno'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                            </div>
                        ) : (
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <Input value={contributor.name || ""}
                                       onChange={(e) => setContributor((prev) => ({
                                           ...prev,
                                           name: e.target.value,
                                       }))}
                                       label={'Razón social'}
                                       containerProps={{
                                           className: '!min-w-0'
                                       }}
                                />
                            </div>
                        )}
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-300 text-sm mt-3 mb-6 font-bold uppercase">
                            NOTAS DE MOVIMIENTOS DEL PREDIO
                        </h6>
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-12/12 px-0">
                                <div className="relative w-full mb-3">
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                    >
                                        Notas
                                    </Typography>
                                    <textarea
                                        type="text"
                                        className="border-0 px-3 py-3 placeholder-blueGray-300 text-blue-gray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        value={lotNotes || ""}
                                        onChange={(e) => setLotNotes(e.target.value)}
                                        rows="6"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <Button
                            className="w-full bg-green-500 mt-4 hover:bg-green-600 text-white rounded-md px-5 py-3 font-semibold"
                            onClick={() => handleSubmit()}
                            disabled={loaderActive}
                        >
                            GUARDAR
                        </Button>
                        {
                            error && <p style={{color: "red"}}>{error}</p>
                        }
                    </CardBody>
                </Card>
            </div>
            <Dialog open={openEndDialog} handler={setOpenEndDialog}>
                <DialogHeader>Guardado</DialogHeader>
                <DialogBody>
                    <Typography>
                        Se corrigieron los datos del predio
                    </Typography>
                </DialogBody>
                <DialogFooter>
                    <Button
                        className="bg-green-500 text-white"
                        onClick={() => handleReset()}
                        disabled={loaderActive}
                    >
                        Finalizar
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    )
};

export default DataEntryCorrection;
